'use client';

import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

export interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose: () => void;
}

function Toast({ message, type, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Start animation after component mounts
    setTimeout(() => setIsAnimating(true), 10);

    if (message) {
      const timer = setTimeout(() => {
        handleClose();
      }, 3000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300);
  };

  if (!isVisible) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          background: '#0f766e',
          icon: <FontAwesomeIcon icon={faCheckCircle} className="text-xl" />
        };
      case 'error':
        return {
          background: '#dc2626',
          icon: <FontAwesomeIcon icon={faExclamationCircle} className="text-xl" />
        };
      case 'warning':
        return {
          background: '#b45309',
          icon: <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl" />
        };
      case 'info':
      default:
        return {
          background: '#2563eb',
          icon: <FontAwesomeIcon icon={faInfoCircle} className="text-xl" />
        };
    }
  };

  const typeStyles = getTypeStyles();

  return (
    <div
      className={`fixed top-4 right-4 z-[100] flex items-center space-x-3 px-4 py-3 rounded-xl shadow-lg backdrop-blur-sm border border-white/20 transition-all duration-300 ease-out ${
        isAnimating ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
      style={{
        background: typeStyles.background,
        color: 'white',
        minWidth: '300px',
        maxWidth: '400px'
      }}
    >
      <div className="mr-2 flex items-center justify-center">{typeStyles.icon}</div>
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button
        onClick={handleClose}
        className="text-white/80 hover:text-white transition-colors duration-200 ml-2"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </div>
  );
}

// Context Definition
interface ToastContextType {
  showToast: (message: string, type?: ToastProps['type']) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Provider Component
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<
    Array<{ id: string; message: string; type: ToastProps['type'] }>
  >([]);

  const showToast = useCallback((message: string, type: ToastProps['type'] = 'info') => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const contextValue = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Hook for consuming the context
export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  // Return showToast directly, and a dummy ToastContainer for backward compatibility
  // This allows us to update the provider first without breaking existing components immediately
  // But we should remove ToastContainer usage from components
  return {
    showToast: context.showToast,
    ToastContainer: () => null // No-op component
  };
}
